// src/sections/claimchecker/bobber/BobberEligibility.jsx
import { useEffect, useRef } from 'react';

const MIN_BOBS = 3;
const MAX_BOBS = 3;
const BOB_DURATION = 800; // ms per tug
const CATCH_DURATION = 1400; // ms for catch cinematic

export default function BobberEligibility({ triggerKey, isEligible, onDone }) {
  const wrapRef = useRef(null);

  const resetBobberStates = () => {
    const el = wrapRef.current;
    if (!el) return;
    el.classList.remove('idle', 'splash', 'catch');
  };

  const triggerBobOnce = () => {
    const el = wrapRef.current;
    if (!el) return;
    resetBobberStates();
    // force reflow so animation restarts cleanly
    void el.offsetWidth;
    el.classList.add('splash');
  };

  const triggerCatch = () => {
    const el = wrapRef.current;
    if (!el) return;
    resetBobberStates();
    void el.offsetWidth;
    el.classList.add('catch');
  };

  // start in idle mode once mounted
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.classList.add('idle');
  }, []);

  // run the 1–3 tug sequence + optional catch whenever triggerKey changes
  useEffect(() => {
    if (!triggerKey) return;
    const el = wrapRef.current;
    if (!el) return;

    let cancelled = false;
    const timers = [];

    resetBobberStates();

    const bobCount =
      Math.floor(Math.random() * (MAX_BOBS - MIN_BOBS + 1)) + MIN_BOBS;

    let totalDuration = 0;

    // queue random bobs
    for (let i = 0; i < bobCount; i++) {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          triggerBobOnce();
        }, totalDuration)
      );
      totalDuration += BOB_DURATION;
    }

    if (isEligible) {
      // eligible → cinematic catch
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          triggerCatch();
        }, totalDuration)
      );
      totalDuration += CATCH_DURATION;
    } else {
      // non-eligible: go back to idle after the final bob
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          resetBobberStates();
          el.classList.add('idle');
        }, totalDuration + 100)
      );
      totalDuration += 100;
    }

    // let parent know the full sequence is done (slight padding)
    if (typeof onDone === 'function') {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          onDone();
        }, totalDuration + 200)
      );
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [triggerKey, isEligible, onDone]);

  return (
    <div className="bobber-eligibility">
      <div className="bobber-wrap" ref={wrapRef}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          preserveAspectRatio="xMidYMid meet"
          x="0px"
          y="0px"
          viewBox="0 0 600 600"
        >
          <defs>
            <g id="Layer7_0_FILL">
              <path
                fill="#FFFFFF"
                stroke="none"
                d="M 304.8 216.2 L 304.8 260.6 -0.45 260.6 -0.45 599.3 604.2 599.3 604.2 216.2 304.8 216.2 Z"
              />
            </g>

            <g id="backWater_0_Layer0_0_FILL">
              <path
                fill="#6E54FF"
                stroke="none"
                d="M 612.45 -45.2 L -612.45 -45.2 -612.45 22.85 163.35 22.85 Q 238.65 67.6 322.4 22.85 L 612.45 22.85 612.45 -45.2 Z"
              />
            </g>

            <g id="rippleBack_0_Layer0_0_FILL">
              <path
                fill="#FFFFFF"
                stroke="none"
                d="M 0.05 -32.55 Q 42.75 -32.55 72.9 -22.1 99.85 -12.8 102.75 0 L 111.3 0 Q 108.4 -14 78.9 -24.25 46.25 -35.55 0 -35.55 -46.25 -35.55 -78.95 -24.25 -108.4 -14 -111.25 0 L -102.65 0 Q -99.75 -12.8 -72.85 -22.1 -42.65 -32.55 0.05 -32.55 Z"
              />
            </g>

            <g id="bobber_0_Layer0_0_FILL">
              <path
                fill="#CC3300"
                stroke="none"
                d="M -61.6 4.25 Q -62 7.8 -62 11.5 -62 37.2 -43.85 55.35 -27.55 71.65 -5.1 73.35 -2.6 73.5 0 73.5 0.75 73.5 1.5 73.5 26.25 72.95 43.85 55.35 62 37.2 62 11.5 62 7.9 61.65 4.45 6 29.7 -61.6 4.25 M 18 -70 Q 18 -71.45 12.85 -72.5 7.75 -73.5 0.5 -73.5 0.35 -73.5 0.2 -73.5 -6.85 -73.5 -11.9 -72.5 -16.95 -71.45 -17 -70 L -17 -48.3 Q -16.9 -46.9 -11.9 -45.9 -6.75 -44.85 0.5 -44.85 7.75 -44.85 12.85 -45.9 17.4 -46.8 17.95 -48 L 18 -70 Z"
              />
              <path
                fill="#FFFFFF"
                stroke="none"
                d="M 12.85 -45.9 Q 7.75 -44.85 0.5 -44.85 -6.75 -44.85 -11.9 -45.9 -16.9 -46.9 -17 -48.3 -32.05 -44.15 -43.85 -32.35 -59.4 -16.8 -61.6 4.25 6 29.7 61.65 4.45 59.45 -16.75 43.85 -32.35 32.4 -43.8 17.95 -48 17.4 -46.8 12.85 -45.9 Z"
              />
            </g>

            <g id="foregroundWater_0_Layer0_0_FILL">
              <path
                fill="#6E54FF"
                fillOpacity="0.7176470588235294"
                stroke="none"
                d="M 323 -161.5 Q 239.25 -116.75 163.95 -161.5 L -613.05 -161.5 -613.05 161.55 613.05 161.55 613.05 -161.5 323 -161.5 Z"
              />
            </g>

            <g id="ripple_0_Layer0_0_FILL">
              <path
                fill="#FFFFFF"
                stroke="none"
                d="M -102.65 -3.1 L -111.3 -3.1 Q -111.6 -1.572265625 -111.6 0 -111.6 16 -78.95 27.3 -46.25 38.65 0 38.65 46.25 38.65 78.9 27.3 111.6 16 111.6 0 111.6 -1.572265625 111.25 -3.1 L 102.75 -3.1 Q 103.1 -1.550390625 103.1 0.05 103.1 14.8 72.9 25.25 42.75 35.75 0.05 35.75 -42.65 35.75 -72.85 25.25 -103 14.8 -103 0.05 -103 -1.550390625 -102.65 -3.1 Z"
              />
            </g>

            <path
              id="line_0_Layer0_0_1_STROKES"
              stroke="#FFFFFF"
              strokeWidth="1"
              strokeLinejoin="round"
              strokeLinecap="round"
              fill="none"
              d="M 0 301.85 L 0 2.85 Q 18.35 -61.2 122.15 2.4 243 76.55 368 36.95 370.5 36.15 373 35.35"
            />
          </defs>

          <g transform="matrix(1,0,0,1,0,0)">
            <use xlinkHref="#Layer7_0_FILL" />
          </g>

          <g id="backWater" transform="matrix(1,0,0,1,-8.25,257.25)">
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#backWater_0_Layer0_0_FILL" />
            </g>
          </g>

          <g id="line" transform="matrix(1,0,0,1,231.2,-399.3)">
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#line_0_Layer0_0_1_STROKES" />
            </g>
          </g>

          <g
            id="rippleBack"
            transform="matrix(0.69610595703125,0,0,0.69610595703125,233.4,281.25)"
          >
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#rippleBack_0_Layer0_0_FILL" />
            </g>
          </g>

          <g id="bobber" transform="matrix(1,0,0,1,231,-76.55)">
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#bobber_0_Layer0_0_FILL" />
            </g>
          </g>

          <g id="foreWater" transform="matrix(1,0,0,1,-8.85,441)">
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#foregroundWater_0_Layer0_0_FILL" />
            </g>
          </g>

          <g
            id="rippleFront"
            transform="matrix(0.69610595703125,0,0,0.69610595703125,233.4,283.2)"
          >
            <g transform="matrix(1,0,0,1,0,0)">
              <use xlinkHref="#ripple_0_Layer0_0_FILL" />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}
